import type { CreatureVitalityCompileInput, CreatureVitalityCompileResult } from '../types';
import { CREATURE_SIZE_IDS } from './creature-size';

// Exact mirror of additions.php Size::LIST. Unknown or case-mismatched ids use Medium's d8,
// matching Size::getVDie() through its case-sensitive HasById lookup.
const V_DIE_BY_SIZE_ID: Readonly<Record<string, number>> = Object.freeze({
    [CREATURE_SIZE_IDS[0]]: 4,
    [CREATURE_SIZE_IDS[1]]: 6,
    [CREATURE_SIZE_IDS[2]]: 8,
    [CREATURE_SIZE_IDS[3]]: 10,
    [CREATURE_SIZE_IDS[4]]: 12,
    [CREATURE_SIZE_IDS[5]]: 20,
    [CREATURE_SIZE_IDS[6]]: 50,
    [CREATURE_SIZE_IDS[7]]: 100,
});

function phpRound(value: number): number {
    return value < 0 ? -Math.round(-value) : Math.round(value);
}

export function getVitalityDieFromCreatureSize(sizeId: string): number {
    return Object.prototype.hasOwnProperty.call(V_DIE_BY_SIZE_ID, sizeId) ? V_DIE_BY_SIZE_ID[sizeId]! : 8;
}

export function calculateAverageVitalityFromDice(
    sizeId: string,
    vDice: number,
    body: number,
    bonus: number = 0,
): number {
    const vDie = getVitalityDieFromCreatureSize(sizeId);
    return Math.floor((vDice * ((vDie + 1) / 2)) + (vDice * body) + bonus);
}

export function calculateMaximumVitalityFromDice(
    sizeId: string,
    vDice: number,
    body: number,
    bonus: number = 0,
): number {
    const vDie = getVitalityDieFromCreatureSize(sizeId);
    return Math.floor((vDice * vDie) + (vDice * body) + bonus);
}

/**
 * Pure Slice 1 compile island. It mirrors only the creature vitality block in
 * finalize_creature_arr(); later status/temp-vitality work in build_v_arr() is deliberately outside it.
 */
export function compileCreatureVitality(input: CreatureVitalityCompileInput): CreatureVitalityCompileResult {
    let vDice = Math.trunc(input.vDice);
    const body = Math.trunc(input.body);
    const vMaxInputIsBlank = input.vMax === null;

    if (vMaxInputIsBlank && input.vDiceMultiplier > 0) {
        vDice = Math.trunc(phpRound(vDice * input.vDiceMultiplier));
    }

    const vDie = getVitalityDieFromCreatureSize(input.sizeId);
    const vAvg = calculateAverageVitalityFromDice(input.sizeId, vDice, body, input.bonus);
    const vMaxFromDice = calculateMaximumVitalityFromDice(input.sizeId, vDice, body, input.bonus);

    let vMax: number;
    let vCurrent: number;
    if (!input.isEncounterCreature || vMaxInputIsBlank) {
        vMax = Math.max(1, vAvg);
        vCurrent = vMax;
    } else {
        vMax = Math.max(1, Math.trunc(input.vMax ?? 0));
        vCurrent = input.vCurrent === null ? vMax : Math.trunc(input.vCurrent);
    }

    return {
        v_die: vDie,
        v_avg: vAvg,
        v_max_from_v_dice: vMaxFromDice,
        v_current: vCurrent,
        v_max: vMax,
    };
}
